import { useNavigate } from 'react-router-dom';
import type { BreadcrumbSegment } from '@/shared/types';

// Breadcrumb — renders a list of segments left-to-right.
//   - segments with `static: true` are non-interactive plain text
//     (e.g. the PFS stage segment)
//   - segments with `hash` and not `static` navigate via React Router
//   - the last segment renders in `crumb active` style regardless
// Separator is a chevron-right glyph rendered between segments.
export function Breadcrumb({ segments }: { segments: BreadcrumbSegment[] }) {
  const navigate = useNavigate();
  if (!segments || segments.length === 0) {
    return (
      <nav data-comment-anchor="Breadcrumb" aria-label="Breadcrumb" className="flex items-center text-xs">
        <span style={{ color: 'var(--ink-tertiary)' }}>—</span>
      </nav>
    );
  }
  return (
    <nav
      data-comment-anchor="Breadcrumb"
      aria-label="Breadcrumb"
      className="flex items-center text-xs whitespace-nowrap overflow-hidden"
    >
      {segments.map((seg, i) => {
        const isLast = i === segments.length - 1;
        const isStatic = seg.static === true;
        const clickable = !isStatic && !!seg.hash;

        const className = ['crumb', isLast ? 'active' : '', isStatic ? 'crumb-static' : '']
          .filter(Boolean)
          .join(' ');

        const onClick = clickable && seg.hash ? () => navigate(seg.hash as string) : undefined;

        const node = clickable ? (
          <button
            key={i}
            type="button"
            data-comment-nav="true"
            className={className}
            onClick={onClick}
            aria-current={isLast ? 'page' : undefined}
          >
            {seg.label}
          </button>
        ) : (
          <span
            key={i}
            className={className}
            aria-disabled={isStatic ? 'true' : undefined}
            aria-current={isLast ? 'page' : undefined}
          >
            {seg.label}
          </span>
        );

        return (
          <span key={`wrap-${i}`} className="inline-flex items-center">
            {node}
            {!isLast && <span className="crumb-sep">›</span>}
          </span>
        );
      })}
    </nav>
  );
}
